/**
 * API service for file upload and management operations.
 * 
 * Provides methods for uploading, retrieving, updating, and deleting files
 * with proper authentication and error handling.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

/**
 * Get authentication headers for API requests
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  return {
    'Authorization': `Basic ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Get authentication headers for file upload (without Content-Type)
 */
const getFileUploadHeaders = () => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  return {
    'Authorization': `Basic ${token}`
    // Don't set Content-Type for multipart/form-data - browser will set it automatically
  };
};

/**
 * Handle API response and errors
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }
  
  // Handle different content types
  const contentType = response.headers.get('content-type');
  
  if (contentType && contentType.includes('application/json')) {
    return await response.json();
  } else if (contentType && contentType.startsWith('image/')) {
    return response.blob();
  } else if (contentType && contentType.includes('application/pdf')) {
    return response.blob();
  }
  
  return response;
};

/**
 * Upload a file
 */
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await fetch(`${API_BASE_URL}/api/files/upload`, {
    method: 'POST',
    headers: getFileUploadHeaders(),
    body: formData
  });
  
  return handleResponse(response);
};

/**
 * Upload an attachment directly to Google Drive attachments folder
 */
export const uploadAttachment = async (file) => {
  // Import Google Drive API dynamically to avoid circular dependencies
  const googleDriveAPI = (await import('./googleDriveAPI')).default;
  
  // Use direct Google Drive upload instead of backend API
  const result = await googleDriveAPI.uploadAttachment(file);
  
  return {
    success: true,
    ...result
  };
};

/**
 * Get file by ID (returns blob for display)
 */
export const getFile = async (fileId) => {
  const response = await fetch(`${API_BASE_URL}/api/files/${fileId}`, {
    method: 'GET',
    headers: getFileUploadHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Get file metadata by ID
 */
export const getFileMetadata = async (fileId) => {
  const response = await fetch(`${API_BASE_URL}/api/files/${fileId}/metadata`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Update file metadata (positioning, sizing, caption)
 */
export const updateFileMetadata = async (fileId, metadata) => {
  const response = await fetch(`${API_BASE_URL}/api/files/${fileId}/metadata`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(metadata)
  });
  
  return handleResponse(response);
};

/**
 * Delete file
 */
export const deleteFile = async (fileId) => {
  const response = await fetch(`${API_BASE_URL}/api/files/${fileId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Get user's files with pagination
 */
export const getUserFiles = async (page = 0, size = 20) => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    sort: 'createdAt,desc'
  });
  
  const response = await fetch(`${API_BASE_URL}/api/files?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Get files by type (image or pdf)
 */
export const getFilesByType = async (type) => {
  const response = await fetch(`${API_BASE_URL}/api/files/by-type/${type}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Get recent files
 */
export const getRecentFiles = async () => {
  const response = await fetch(`${API_BASE_URL}/api/files/recent`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Search files by filename
 */
export const searchFiles = async (searchTerm) => {
  const params = new URLSearchParams({
    q: searchTerm
  });
  
  const response = await fetch(`${API_BASE_URL}/api/files/search?${params}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Get file statistics
 */
export const getFileStatistics = async () => {
  const response = await fetch(`${API_BASE_URL}/api/files/stats`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Generate file URL for display
 */
export const getFileUrl = (fileId) => {
  // For browser image loading, we need to handle auth differently
  // The browser won't send custom headers for img src requests
  // We'll need to create a blob URL or use a different approach
  return `${API_BASE_URL}/api/files/${fileId}`;
};

/**
 * Create object URL from file blob for display
 */
export const createFileObjectUrl = async (fileId) => {
  try {
    const blob = await getFile(fileId);
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Failed to create object URL for file:', fileId, error);
    throw error;
  }
};

/**
 * Validate file before upload
 */
export const validateFile = (file) => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf'
  ];
  
  if (!file) {
    throw new Error('No file selected');
  }
  
  if (file.size > maxSize) {
    throw new Error('File size exceeds 10MB limit');
  }
  
  if (!allowedTypes.includes(file.type)) {
    throw new Error('File type not supported. Please upload images (JPEG, PNG, GIF, WebP) or PDF files.');
  }
  
  return true;
};

export default {
  uploadFile,
  uploadAttachment,
  getFile,
  getFileMetadata,
  updateFileMetadata,
  deleteFile,
  getUserFiles,
  getFilesByType,
  getRecentFiles,
  searchFiles,
  getFileStatistics,
  getFileUrl,
  createFileObjectUrl,
  validateFile
}; 